/**
=========================================================
* STAIL - Dialog Styles Utility
=========================================================
* Standardized dialog/popup header and content styles
* Created: November 6, 2025
=========================================================
*/

import brandGradients from 'assets/theme/base/gradients';
import colors from 'assets/theme/base/colors';

/**
 * UNIFIED dialog header styles - ONE COLOR FOR ALL POPUPS.
 * Dark navy gradient matching Frontpage background theme.
 * Gold text for high contrast and visual hierarchy.
 * 
 * Usage:
 * import { dialogHeaderStyles } from 'utils/dialogStyles';
 * <DialogTitle sx={dialogHeaderStyles.default}>Title</DialogTitle>
 */
export const dialogHeaderStyles = {
  // Default - DARK NAVY GRADIENT (same for ALL dialogs)
  default: { 
    background: brandGradients.navyGlassDark, 
    color: '#f4c542', // gold text for contrast
    fontWeight: 600,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
  },
  
  // Success - SAME NAVY GRADIENT
  success: { 
    background: brandGradients.navyGlassDark, 
    color: '#f4c542',
    fontWeight: 600,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
  },
  
  // Warning - SAME NAVY GRADIENT
  warning: { 
    background: brandGradients.navyGlassDark, 
    color: '#f4c542',
    fontWeight: 600,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
  },
  
  // Error - SAME NAVY GRADIENT
  error: { 
    background: brandGradients.navyGlassDark, 
    color: '#f4c542',
    fontWeight: 600,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
  },
  
  // Special - SAME NAVY GRADIENT
  special: { 
    background: brandGradients.navyGlassDark, 
    color: '#f4c542',
    fontWeight: 600,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
  },
  
  // Info - SAME NAVY GRADIENT
  info: {
    background: brandGradients.navyGlassDark,
    color: '#f4c542',
    fontWeight: 600,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
  },
  
  // Report - SAME NAVY GRADIENT
  report: {
    background: brandGradients.navyGlassDark,
    color: '#f4c542',
    fontWeight: 600,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
  },
  
  // Profile - SAME NAVY GRADIENT
  profile: {
    background: brandGradients.navyGlassDark,
    color: '#f4c542',
    fontWeight: 600,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
  },
};

/**
 * Standardized dialog content styles.
 * Ensures consistent spacing and background colors.
 */
export const dialogContentStyles = {
  // Default white background with standard padding
  default: {
    backgroundColor: '#fff',
  },
  
  // Light grey background for forms
  form: {
    backgroundColor: colors.brandColors.lightGrey,
  },
  
  // Alternative light grey
  light: {
    backgroundColor: colors.brandColors.lightGreyAlt,
  },
};

/**
 * Standardized dialog action button styles.
 * Matches the semantic meaning of the action.
 */
export const dialogActionStyles = {
  // Save/Submit button (green gradient)
  save: {
    background: brandGradients.greenSuccess,
    color: '#fff',
    '&:hover': {
      opacity: 0.9,
    },
  },
  
  // Cancel/Close button (red gradient)
  cancel: {
    background: brandGradients.redError,
    color: '#fff',
    '&:hover': {
      opacity: 0.9,
    },
  },
  
  // Primary action button (blue gradient)
  primary: {
    background: brandGradients.blue,
    color: '#fff',
    '&:hover': {
      opacity: 0.9,
    },
  },
  
  // Print/Export button (blue gradient)
  print: {
    background: brandGradients.blue,
    color: '#fff',
    '&:hover': {
      opacity: 0.9,
    },
  },
};

/**
 * Complete dialog style presets combining header, content, and actions.
 * Use these for common dialog patterns.
 */
export const dialogPresets = {
  // Standard add/create dialog
  add: {
    header: dialogHeaderStyles.special,
    content: dialogContentStyles.form,
    saveButton: dialogActionStyles.save,
    cancelButton: dialogActionStyles.cancel,
  },
  
  // Standard edit/update dialog
  edit: {
    header: dialogHeaderStyles.warning,
    content: dialogContentStyles.form,
    saveButton: dialogActionStyles.save,
    cancelButton: dialogActionStyles.cancel,
  },
  
  // Delete confirmation dialog
  delete: {
    header: dialogHeaderStyles.error,
    content: dialogContentStyles.default,
    confirmButton: dialogActionStyles.cancel,
    cancelButton: dialogActionStyles.primary,
  },
  
  // View/Info dialog
  view: {
    header: dialogHeaderStyles.info,
    content: dialogContentStyles.light,
    closeButton: dialogActionStyles.primary,
  },
  
  // Print/Report dialog
  print: {
    header: dialogHeaderStyles.report,
    content: dialogContentStyles.default,
    printButton: dialogActionStyles.print,
    closeButton: dialogActionStyles.cancel,
  },
};

export default dialogHeaderStyles;
